/**
 * Hit-test for aligning pointer X with key moments laid out linearly along the slider
 * (same domain as `@dtvgo/progress-bar` — seconds 0 … durationSeconds).
 */

/** ± pixels from slit center to activate preview (mousemove on bar). */
export const KEY_MOMENT_BAR_POINTER_HALF_WIDTH_PX = 22;

/**
 * @param {{
 *   items: Array<{ timestamp: number }>,
 *   streamStartTimestamp: number,
 *   durationSeconds: number,
 *   pxAlongStrip: number,
 *   stripWidthPx: number,
 *   hitHalfWidthPx?: number,
 * }} opts
 * @returns {{ item: object, positionPct: number } | null}
 */
export default function nearestKeyMomentOnBar({
  items,
  streamStartTimestamp,
  durationSeconds,
  pxAlongStrip,
  stripWidthPx,
  hitHalfWidthPx = KEY_MOMENT_BAR_POINTER_HALF_WIDTH_PX,
}) {
  if (!items?.length || durationSeconds <= 0 || stripWidthPx <= 0 || !streamStartTimestamp) {
    return null;
  }

  const half = Number.isFinite(hitHalfWidthPx) && hitHalfWidthPx > 0 ? hitHalfWidthPx : 22;

  const candidates = items
    .map((item) => {
      const offsetSeconds = item.timestamp - streamStartTimestamp;
      if (!Number.isFinite(offsetSeconds)) return null;
      const pxAtMoment = (offsetSeconds / durationSeconds) * stripWidthPx;
      const dx = Math.abs(pxAlongStrip - pxAtMoment);
      if (dx > half) return null;
      return {
        dx,
        item,
        rawPct: (offsetSeconds / durationSeconds) * 100,
      };
    })
    .filter(Boolean);

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => a.dx - b.dx);

  const { item, rawPct } = candidates[0];
  const positionPct = Math.min(100, Math.max(0, rawPct));

  return { item, positionPct };
}
